import React from 'react';
import { useTranslation } from 'react-i18next';
import { AcademyIcon } from '@common/utils/icons/icons';
import { useAppSelector } from '../../store';
import { selectUser } from '../../store/general/general-slice-selectors';
import NavigationButton from '../ui/NavigationButton';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import OrganizationRole from '../../models/enums/organization-roles';
import { useNavigate } from 'react-router-dom';
import FleetLogo, { SPHERE_INSIGHTS_TITLE } from 'components/fleet-management/FleetLogo';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import SphereNavigationRoutes from '@common/enums/query-params/sphere-navigation-routes';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { navigationOpenStorageName } from './MainNavigationBar';
import { getCurrentPageName, trackEvent } from 'services/analytics';
import { AnalyticsEvent, HelpLink, HelpLinkSource } from 'services/analytics-events';

const AdditionalButtons: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const matchesLgDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

  const user = useAppSelector((state) => selectUser(state));

  const fleetManagementClickHandler = () => {
    localStorage.setItem(navigationOpenStorageName, 'false');
    navigate(SphereNavigationRoutes.Insights);
  };

  const trackHelpLink = (link: HelpLink, source: HelpLinkSource) => {
    trackEvent(AnalyticsEvent.HelpLinkOpened, {
      link,
      source,
      source_page: getCurrentPageName(),
    });
  };

  const hubspotClickHandler = () => {
    trackHelpLink(HelpLink.KnowledgeHub, HelpLinkSource.TopBar);
    window.open(t('link-knowledge-hub'), '_blank', 'noopener');
  };

  const newFeaturesClickHandler = () => {
    trackHelpLink(HelpLink.NewFeatures, HelpLinkSource.TopBar);
    window.open(t('link-new-features'), '_blank', 'noopener');
  };

  // TODO: Sphere Insights is restricted to manufacturer users for the initial release.
  // To open it to dealer level and higher, comment out the manufacturer-only rule
  // below and comment the original rule back in.
  const canAccessFleetManagement = user.data.organization.role === OrganizationRole.MANUFACTURER;
  // const canAccessFleetManagement = [
  //   OrganizationRole.MANUFACTURER,
  //   OrganizationRole.WHOLESALER,
  //   OrganizationRole.IMPORTER,
  //   OrganizationRole.DISTRIBUTOR,
  // ].includes(user.data.organization.role);

  return (
    <React.Fragment>
      {canAccessFleetManagement && (
        <NavigationButton
          isNavOpen={true}
          title={SPHERE_INSIGHTS_TITLE}
          icon={
            <Box>
              <FleetLogo height={2.5} showIcon />
            </Box>
          }
          onClick={fleetManagementClickHandler}
          isTopBar
        />
      )}
      <NavigationButton
        isNavOpen={true}
        title={t('info-new-features')}
        icon={<TipsAndUpdatesRoundedIcon />}
        onClick={newFeaturesClickHandler}
        isTopBar
      />
      {matchesLgDown ? (
        <NavigationButton
          isNavOpen={true}
          title={t('info-kemaro-hubspot')}
          icon={<AcademyIcon />}
          onClick={hubspotClickHandler}
          isTopBar
        />
      ) : (
        <TooltipIconButton
          tooltip={t('info-kemaro-hubspot')}
          icon={<AcademyIcon />}
          onClick={hubspotClickHandler}
        />
      )}
    </React.Fragment>
  );
};

export default AdditionalButtons;
